import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { api, formatApiError } from "../lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import { LogOut, Plus, Trash2, Pencil, Download, Mail, Briefcase, FileText, CheckCircle2, Circle, Eye, FileDown, Link2, FilePlus2, Users, Send, AlertOctagon, Sparkles } from "lucide-react";
import { formatDate } from "../lib/constants";
import { API } from "../lib/api";
import DocumentTemplatesManager from "../components/DocumentTemplatesManager";
import RailanChat from "../components/RailanChat";
import CooperadosManager from "../components/CooperadosManager";
import DispatchesManager from "../components/DispatchesManager";
import BrigadistasManager from "../components/BrigadistasManager";

const CATEGORY_LABEL = {
  comunicado: "Comunicado / Ofício",
  solicitacao: "Solicitação",
  documento: "Documento",
  outro: "Outro",
};

const SENDER_LABEL = {
  cooperado: "Cooperado",
  instituicao: "Instituição",
  outro: "Outro",
};

const STATUS_LABEL = {
  novo: "Novo",
  em_analise: "Em análise",
  aprovado: "Aprovado",
  rejeitado: "Rejeitado",
};

const OPEN_FOR = [
  { key: "todos", label: "Cooperados e não cooperados" },
  { key: "cooperados", label: "Apenas cooperados" },
  { key: "publico", label: "Aberta ao público em geral" },
];

export default function SecretariaPainel() {
  const { user, logout, loading } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    if (loading) return;
    if (!user) {
      nav("/secretaria/login");
    } else if (user.role !== "secretary" && user.role !== "admin") {
      nav("/secretaria/login");
    }
  }, [user, loading, nav]);

  if (loading || !user) return <div className="container-page py-20 text-forest-900/60">Carregando...</div>;

  return (
    <div data-testid="secretaria-painel" className="py-14">
      <div className="container-page">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-10">
          <div>
            <div className="eyebrow mb-2">Secretaria Virtual</div>
            <h1 className="font-display text-4xl text-forest-900">Olá, {user.name || user.email}</h1>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/" className="btn-outline !py-2.5 !px-4">Ver site</Link>
            <button onClick={logout} className="btn-primary !py-2.5 !px-4" data-testid="sec-logout">
              <LogOut className="w-4 h-4" /> Sair
            </button>
          </div>
        </div>

        <Tabs defaultValue="inbox">
          <TabsList className="bg-cream-200 border border-cream-400 flex flex-wrap h-auto">
            <TabsTrigger value="inbox" data-testid="tab-inbox"><Mail className="w-4 h-4 mr-1.5" /> Caixa de entrada</TabsTrigger>
            <TabsTrigger value="cooperados" data-testid="tab-cooperados"><Users className="w-4 h-4 mr-1.5" /> Cooperados</TabsTrigger>
            <TabsTrigger value="dispatches" data-testid="tab-dispatches"><Send className="w-4 h-4 mr-1.5" /> Encaminhamentos</TabsTrigger>
            <TabsTrigger value="jobs" data-testid="tab-jobs"><Briefcase className="w-4 h-4 mr-1.5" /> Vagas</TabsTrigger>
            <TabsTrigger value="applications" data-testid="tab-applications"><FileText className="w-4 h-4 mr-1.5" /> Candidaturas</TabsTrigger>
            <TabsTrigger value="templates" data-testid="tab-templates"><FilePlus2 className="w-4 h-4 mr-1.5" /> Modelos</TabsTrigger>
            <TabsTrigger value="railan" data-testid="tab-railan"><Sparkles className="w-4 h-4 mr-1.5" /> Railan (IA)</TabsTrigger>
            <TabsTrigger value="brigadistas" data-testid="tab-brigadistas">🚒 Brigadistas 2026</TabsTrigger>
            <TabsTrigger value="ombudsman" data-testid="tab-ombudsman"><AlertOctagon className="w-4 h-4 mr-1.5" /> Canal Reclamações</TabsTrigger>
          </TabsList>
          <TabsContent value="inbox"><InboxManager /></TabsContent>
          <TabsContent value="cooperados"><CooperadosManager /></TabsContent>
          <TabsContent value="dispatches"><DispatchesManager /></TabsContent>
          <TabsContent value="jobs"><JobsManager /></TabsContent>
          <TabsContent value="applications"><ApplicationsManager /></TabsContent>
          <TabsContent value="templates"><DocumentTemplatesManager /></TabsContent>
          <TabsContent value="railan"><RailanChat /></TabsContent>
          <TabsContent value="brigadistas"><BrigadistasManager /></TabsContent>
          <TabsContent value="ombudsman"><OmbudsmanManager /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function downloadFile(stored_name) {
  const token = localStorage.getItem("coomflona_token");
  const url = `${API}/files/${encodeURIComponent(stored_name)}`;
  fetch(url, { headers: { Authorization: `Bearer ${token}` } })
    .then((r) => {
      if (!r.ok) throw new Error("Erro ao baixar");
      return r.blob().then((blob) => ({ blob, filename: r.headers.get("content-disposition") }));
    })
    .then(({ blob }) => {
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = stored_name.replace(/^[a-f0-9-]+_/, "");
      link.click();
      setTimeout(() => URL.revokeObjectURL(link.href), 1000);
    })
    .catch(() => toast.error("Não foi possível baixar o arquivo."));
}

function downloadPdf(path, fallbackName) {
  const token = localStorage.getItem("coomflona_token");
  fetch(`${API}${path}`, { headers: { Authorization: `Bearer ${token}` } })
    .then((r) => {
      if (!r.ok) throw new Error("PDF error");
      return r.blob();
    })
    .then((blob) => {
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = fallbackName;
      link.click();
      setTimeout(() => URL.revokeObjectURL(link.href), 1000);
    })
    .catch(() => toast.error("Não foi possível gerar o PDF."));
}

function copyShareLink(jobId) {
  const link = `${window.location.origin}/vagas/${jobId}`;
  navigator.clipboard.writeText(link)
    .then(() => toast.success("Link da vaga copiado!"))
    .catch(() => toast.error("Não foi possível copiar."));
}

/* ---------------- Inbox ---------------- */
function InboxManager() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("");
  const load = () => api.get("/secretariat/messages").then((r) => setItems(r.data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []);

  const markRead = async (id) => { try { await api.post(`/secretariat/messages/${id}/read`); load(); } catch (e) { toast.error(formatApiError(e)); } };
  const del = async (id) => {
    if (!window.confirm("Remover esta mensagem?")) return;
    try { await api.delete(`/secretariat/messages/${id}`); toast.success("Mensagem removida."); load(); } catch (e) { toast.error(formatApiError(e)); }
  };

  const filtered = filter ? items.filter((m) => m.category === filter) : items;

  return (
    <div className="mt-8">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-6">
        <h2 className="font-display text-2xl text-forest-900">Mensagens recebidas ({filtered.length})</h2>
        <div className="flex items-center gap-2 flex-wrap">
          {[{ k: "", l: "Todas" }, ...Object.entries(CATEGORY_LABEL).map(([k, l]) => ({ k, l }))].map((o) => (
            <button
              key={o.k || "all"}
              type="button"
              onClick={() => setFilter(o.k)}
              data-testid={`filter-${o.k || "all"}`}
              className={`text-xs px-3 py-1.5 border transition-colors ${filter === o.k ? "bg-forest-700 text-cream-100 border-forest-700" : "bg-white text-forest-700 border-cream-400 hover:bg-cream-200"}`}
            >
              {o.l}
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-3">
        {filtered.map((m) => (
          <div key={m.id} data-testid={`message-${m.id}`} className={`bg-white border p-5 ${m.read ? "border-cream-400" : "border-copaiba-500"}`}>
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
              <div className="flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  {m.read ? <CheckCircle2 className="w-4 h-4 text-forest-700" /> : <Circle className="w-4 h-4 text-copaiba-500" />}
                  <div className="font-medium text-forest-900">{m.sender_name}</div>
                  <span className="text-xs px-2 py-0.5 bg-cream-200 text-forest-700">{SENDER_LABEL[m.sender_type]}</span>
                  <span className="text-xs px-2 py-0.5 bg-forest-700 text-cream-100">{CATEGORY_LABEL[m.category]}</span>
                </div>
                <div className="text-xs text-forest-900/60 mt-1">
                  {formatDate(m.created_at)} · {m.sender_email}{m.sender_phone ? ` · ${m.sender_phone}` : ""}
                  {m.institution_name ? ` · ${m.institution_name}` : ""}
                </div>
                <div className="font-display text-xl text-forest-900 mt-3">{m.subject}</div>
                <p className="text-forest-900/85 mt-2 whitespace-pre-wrap leading-relaxed">{m.body}</p>
                {m.attachment && (
                  <button
                    onClick={() => downloadFile(m.attachment.stored_name)}
                    data-testid={`download-${m.id}`}
                    className="mt-3 inline-flex items-center gap-2 text-sm text-forest-700 hover:text-copaiba-700"
                  >
                    <Download className="w-4 h-4" /> {m.attachment.original_name}
                    <span className="text-xs text-forest-900/55">({(m.attachment.size / 1024).toFixed(0)} KB)</span>
                  </button>
                )}
              </div>
              <div className="flex md:flex-col gap-2 shrink-0">
                {!m.read && (
                  <button onClick={() => markRead(m.id)} data-testid={`mark-read-${m.id}`} className="p-2 text-forest-700 hover:bg-cream-200" title="Marcar como lida">
                    <CheckCircle2 className="w-4 h-4" />
                  </button>
                )}
                <a
                  href={`mailto:${m.sender_email}?subject=Re: ${encodeURIComponent(m.subject)}`}
                  className="p-2 text-forest-700 hover:bg-cream-200"
                  title="Responder por e-mail"
                  data-testid={`reply-${m.id}`}
                >
                  <Mail className="w-4 h-4" />
                </a>
                <button onClick={() => del(m.id)} data-testid={`delete-${m.id}`} className="p-2 text-terracotta-500 hover:bg-cream-200" title="Excluir">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        ))}
        {filtered.length === 0 && <div className="bg-cream-200 border border-cream-400 p-10 text-center text-forest-900/65">Nenhuma mensagem nesta categoria.</div>}
      </div>
    </div>
  );
}

/* ---------------- Jobs ---------------- */
function JobsManager() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/secretariat/jobs").then((r) => setItems(r.data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []);

  const del = async (id) => {
    if (!window.confirm("Remover esta vaga?")) return;
    try { await api.delete(`/secretariat/jobs/${id}`); toast.success("Vaga removida."); load(); } catch (e) { toast.error(formatApiError(e)); }
  };

  const toggleOpen = async (j) => {
    try { await api.put(`/secretariat/jobs/${j.id}`, { open: !j.open }); load(); } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="mt-8">
      <div className="bg-forest-700 text-cream-100 p-5 mb-6">
        <div className="font-display text-lg mb-1">Como funciona</div>
        <p className="text-cream-100/85 text-sm leading-relaxed">
          Crie uma vaga e clique no ícone <strong>Link</strong> do cartão para copiar o endereço público. Compartilhe-o
          no WhatsApp, e-mail ou Instagram — os candidatos preenchem a inscrição direto pelo site. Para baixar todas as
          inscrições recebidas em PDF, clique no ícone <strong>PDF</strong> do cartão da vaga.
        </p>
      </div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-display text-2xl text-forest-900">Vagas cadastradas ({items.length})</h2>
        <Button onClick={() => { setEditing(null); setOpen(true); }} data-testid="add-job-button" className="bg-forest-700 hover:bg-forest-900 text-cream-100 rounded-none">
          <Plus className="w-4 h-4 mr-2" /> Nova vaga
        </Button>
      </div>

      <div className="grid md:grid-cols-2 gap-5">
        {items.map((j) => (
          <div key={j.id} data-testid={`job-row-${j.id}`} className={`bg-white border p-5 ${j.open ? "border-cream-400" : "border-terracotta-500/40"}`}>
            <div className="flex items-start justify-between gap-3 mb-2">
              <div className="flex-1">
                <span className={`inline-block text-xs px-2 py-0.5 mb-2 ${j.open ? "bg-forest-700 text-cream-100" : "bg-terracotta-500 text-cream-100"}`}>
                  {j.open ? "Aberta" : "Encerrada"}
                </span>
                <div className="font-display text-xl text-forest-900 leading-tight">{j.title}</div>
                <div className="text-xs text-forest-900/65 mt-1">
                  {j.community || "—"} · {j.contract_type || "—"} · {formatDate(j.created_at)}
                </div>
              </div>
            </div>
            <p className="text-sm text-forest-900/75 line-clamp-3 mt-3">{j.description}</p>
            <div className="flex items-center gap-2 mt-4 pt-3 border-t border-cream-400">
              <button onClick={() => { setEditing(j); setOpen(true); }} data-testid={`edit-job-${j.id}`} className="p-2 text-forest-700 hover:bg-cream-200" title="Editar"><Pencil className="w-4 h-4" /></button>
              <button onClick={() => toggleOpen(j)} data-testid={`toggle-job-${j.id}`} className="p-2 text-forest-700 hover:bg-cream-200" title={j.open ? "Encerrar vaga" : "Reabrir vaga"}><Eye className="w-4 h-4" /></button>
              <button onClick={() => copyShareLink(j.id)} data-testid={`copy-link-${j.id}`} className="p-2 text-forest-700 hover:bg-cream-200" title="Copiar link da vaga"><Link2 className="w-4 h-4" /></button>
              <button onClick={() => downloadPdf(`/secretariat/jobs/${j.id}/pdf`, `inscricoes_${j.title}.pdf`)} data-testid={`pdf-job-${j.id}`} className="p-2 text-forest-700 hover:bg-cream-200" title="Baixar todas as inscrições em PDF"><FileDown className="w-4 h-4" /></button>
              <button onClick={() => del(j.id)} data-testid={`delete-job-${j.id}`} className="p-2 text-terracotta-500 hover:bg-cream-200" title="Excluir"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="md:col-span-2 bg-cream-200 border border-cream-400 p-10 text-center text-forest-900/65">Nenhuma vaga cadastrada ainda.</div>}
      </div>

      <JobDialog open={open} onOpenChange={setOpen} editing={editing} onSaved={() => { setOpen(false); load(); }} />
    </div>
  );
}

function JobDialog({ open, onOpenChange, editing, onSaved }) {
  const [form, setForm] = useState({ title: "", description: "", community: "", contract_type: "", deadline: "", open_for: "todos", open: true });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (editing) setForm({ ...editing });
    else setForm({ title: "", description: "", community: "", contract_type: "", deadline: "", open_for: "todos", open: true });
  }, [editing, open]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) { await api.put(`/secretariat/jobs/${editing.id}`, form); toast.success("Vaga atualizada."); }
      else { await api.post("/secretariat/jobs", form); toast.success("Vaga publicada!"); }
      onSaved();
    } catch (err) { toast.error(formatApiError(err)); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{editing ? "Editar vaga" : "Nova vaga"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div><Label>Título da vaga</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required data-testid="job-form-title" /></div>
          <div><Label>Descrição completa</Label><Textarea rows={6} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} required data-testid="job-form-description" /></div>
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Comunidade / Local</Label><Input value={form.community} onChange={(e) => setForm({ ...form, community: e.target.value })} data-testid="job-form-community" /></div>
            <div><Label>Tipo de contrato</Label><Input value={form.contract_type} onChange={(e) => setForm({ ...form, contract_type: e.target.value })} placeholder="CLT, Temporário, Voluntário..." data-testid="job-form-contract" /></div>
            <div><Label>Prazo de inscrições</Label><Input value={form.deadline} onChange={(e) => setForm({ ...form, deadline: e.target.value })} placeholder="Ex.: 30/06/2026" data-testid="job-form-deadline" /></div>
            <div>
              <Label>Aberta para</Label>
              <Select value={form.open_for} onValueChange={(v) => setForm({ ...form, open_for: v })}>
                <SelectTrigger data-testid="job-form-openfor"><SelectValue /></SelectTrigger>
                <SelectContent>{OPEN_FOR.map((o) => <SelectItem key={o.key} value={o.key}>{o.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex items-center gap-3 pt-2">
            <Switch checked={form.open} onCheckedChange={(v) => setForm({ ...form, open: v })} data-testid="job-form-open" />
            <Label>Vaga aberta (visível no site)</Label>
          </div>
          <DialogFooter>
            <Button type="submit" disabled={saving} data-testid="job-form-submit" className="bg-forest-700 hover:bg-forest-900 text-cream-100 rounded-none">
              {saving ? "Salvando..." : "Salvar vaga"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/* ---------------- Applications ---------------- */
function ApplicationsManager() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/secretariat/applications").then((r) => setItems(r.data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    try {
      const fd = new FormData(); fd.append("status", status);
      await api.post(`/secretariat/applications/${id}/status`, fd);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const del = async (id) => {
    if (!window.confirm("Remover esta candidatura?")) return;
    try { await api.delete(`/secretariat/applications/${id}`); toast.success("Removida."); load(); } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="mt-8">
      <h2 className="font-display text-2xl text-forest-900 mb-5">Candidaturas recebidas ({items.length})</h2>
      <div className="space-y-3">
        {items.map((a) => (
          <div key={a.id} data-testid={`application-${a.id}`} className="bg-white border border-cream-400 p-5">
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
              <div className="flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`text-xs px-2 py-0.5 ${
                    a.status === "aprovado" ? "bg-forest-700 text-cream-100" :
                    a.status === "rejeitado" ? "bg-terracotta-500 text-cream-100" :
                    a.status === "em_analise" ? "bg-copaiba-500 text-forest-900" :
                    "bg-cream-300 text-forest-700"
                  }`}>{STATUS_LABEL[a.status]}</span>
                  <span className="text-xs text-forest-900/65">{formatDate(a.created_at)}</span>
                  {a.is_cooperado && <span className="text-xs px-2 py-0.5 bg-cream-200 text-forest-700">Cooperado(a)</span>}
                </div>
                <div className="font-display text-xl text-forest-900 mt-2">{a.name}</div>
                <div className="text-sm text-forest-900/75">{a.email} · {a.phone || "sem telefone"} · {a.community || "comunidade não informada"}</div>
                <div className="text-xs uppercase tracking-[0.18em] text-forest-900/65 mt-3">Candidatura para</div>
                <div className="text-forest-900">{a.job_title}</div>
                {a.message && <p className="text-sm text-forest-900/80 mt-3 whitespace-pre-wrap leading-relaxed">{a.message}</p>}
                {a.cv && (
                  <button onClick={() => downloadFile(a.cv.stored_name)} data-testid={`cv-download-${a.id}`} className="mt-3 inline-flex items-center gap-2 text-sm text-forest-700 hover:text-copaiba-700">
                    <Download className="w-4 h-4" /> {a.cv.original_name}
                  </button>
                )}
                <div className="mt-3">
                  <button
                    onClick={() => downloadPdf(`/secretariat/applications/${a.id}/pdf`, `inscricao_${a.name}.pdf`)}
                    data-testid={`pdf-application-${a.id}`}
                    className="inline-flex items-center gap-2 text-sm text-forest-700 hover:text-copaiba-700 underline"
                  >
                    <FileDown className="w-4 h-4" /> Baixar ficha de inscrição (PDF)
                  </button>
                </div>
              </div>
              <div className="flex flex-col gap-2 shrink-0">
                <Select value={a.status} onValueChange={(v) => setStatus(a.id, v)}>
                  <SelectTrigger data-testid={`status-select-${a.id}`} className="w-44"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(STATUS_LABEL).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
                  </SelectContent>
                </Select>
                <button onClick={() => del(a.id)} data-testid={`delete-app-${a.id}`} className="text-sm text-terracotta-500 hover:underline self-end">Excluir</button>
              </div>
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="bg-cream-200 border border-cream-400 p-10 text-center text-forest-900/65">Nenhuma candidatura recebida ainda.</div>}
      </div>
    </div>
  );
}


/* --------- Canal de Reclamações (Ouvidoria) --------- */
const OUV_STATUS = [
  { v: "nova", label: "Nova" },
  { v: "em_analise", label: "Em análise" },
  { v: "encaminhada", label: "Encaminhada" },
  { v: "respondida", label: "Respondida" },
  { v: "arquivada", label: "Arquivada" },
];

function OmbudsmanManager() {
  const [data, setData] = React.useState({ items: [], stats: {} });
  const [statusFilter, setStatusFilter] = React.useState("");
  const [tipoFilter, setTipoFilter] = React.useState("");
  const [selected, setSelected] = React.useState(null);

  const load = () => {
    const qs = new URLSearchParams();
    if (statusFilter) qs.set("status", statusFilter);
    if (tipoFilter) qs.set("tipo", tipoFilter);
    api.get(`/secretariat/ombudsman/manifestacoes?${qs.toString()}`)
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiError(e)));
  };

  React.useEffect(load, [statusFilter, tipoFilter]);

  const changeStatus = async (mid, status) => {
    try {
      await api.patch(`/secretariat/ombudsman/manifestacoes/${mid}/status`, { status });
      toast.success("Status atualizado.");
      load();
      if (selected?.id === mid) setSelected({ ...selected, status });
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (mid) => {
    if (!window.confirm("Excluir esta manifestação?")) return;
    try {
      await api.delete(`/secretariat/ombudsman/manifestacoes/${mid}`);
      toast.success("Removida.");
      setSelected(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const s = data.stats || {};
  return (
    <div className="mt-6" data-testid="ombudsman-manager">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-5">
        <div className="bg-forest-700 text-cream-100 p-3">
          <div className="font-display text-3xl text-copaiba-300">{s.total || 0}</div>
          <div className="text-xs uppercase tracking-[0.18em]">Total</div>
        </div>
        <div className="bg-white border border-cream-400 p-3">
          <div className="font-display text-3xl text-forest-900">{s.nova || 0}</div>
          <div className="text-xs uppercase tracking-[0.18em] text-forest-900/65">Novas</div>
        </div>
        <div className="bg-white border border-cream-400 p-3">
          <div className="font-display text-3xl text-forest-900">{s.em_analise || 0}</div>
          <div className="text-xs uppercase tracking-[0.18em] text-forest-900/65">Em análise</div>
        </div>
        <div className="bg-white border border-cream-400 p-3">
          <div className="font-display text-3xl text-forest-900">{s.respondida || 0}</div>
          <div className="text-xs uppercase tracking-[0.18em] text-forest-900/65">Respondidas</div>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="ouv-filter-status" className="bg-cream-100 border border-cream-400 px-3 py-2 text-sm">
          <option value="">Todos status</option>
          {OUV_STATUS.map((x) => <option key={x.v} value={x.v}>{x.label}</option>)}
        </select>
        <select value={tipoFilter} onChange={(e) => setTipoFilter(e.target.value)} data-testid="ouv-filter-tipo" className="bg-cream-100 border border-cream-400 px-3 py-2 text-sm">
          <option value="">Todos tipos</option>
          <option value="reclamacao">Reclamação</option>
          <option value="denuncia">Denúncia</option>
          <option value="sugestao">Sugestão</option>
          <option value="elogio">Elogio</option>
        </select>
      </div>

      <div className="space-y-2">
        {(data.items || []).map((it) => (
          <div key={it.id} className="bg-white border border-cream-400 p-3 flex flex-wrap items-start justify-between gap-3" data-testid={`ouv-item-${it.id}`}>
            <div className="flex-1 min-w-[240px]">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-mono text-xs text-forest-700">{it.protocol}</span>
                <span className="text-xs px-2 py-0.5 bg-forest-900 text-cream-100 uppercase tracking-[0.15em]">{it.tipo_label}</span>
                {it.anonymous && <span className="text-xs px-2 py-0.5 bg-copaiba-300 text-forest-900">anônima</span>}
                <span className={`text-xs px-2 py-0.5 ${it.status === "nova" ? "bg-terracotta-500 text-cream-100" : "bg-cream-400 text-forest-900"}`}>
                  {OUV_STATUS.find((x) => x.v === it.status)?.label || it.status}
                </span>
              </div>
              <div className="text-sm text-forest-900 mt-1"><b>{it.name || (it.anonymous ? "(Anônima)" : "—")}</b> · {it.community || "—"}</div>
              <div className="text-xs text-forest-900/65 mt-1 line-clamp-2">{it.description}</div>
              {it.contact && <div className="text-xs text-forest-900/60 mt-1">Contato: {it.contact}</div>}
            </div>
            <div className="flex items-center gap-1">
              <button onClick={() => setSelected(it)} data-testid={`ouv-view-${it.id}`} className="p-2 hover:bg-cream-200"><Eye className="w-4 h-4" /></button>
              <button onClick={() => remove(it.id)} data-testid={`ouv-del-${it.id}`} className="p-2 text-terracotta-500 hover:bg-cream-200"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
        {(data.items || []).length === 0 && (
          <div className="bg-cream-200 border border-cream-400 p-10 text-center text-forest-900/65">Nenhuma manifestação recebida ainda.</div>
        )}
      </div>

      {selected && (
        <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-display text-2xl">{selected.tipo_label} — {selected.protocol}</DialogTitle>
            </DialogHeader>
            <div className="space-y-2 text-sm">
              <div><b>Solicitante:</b> {selected.name || (selected.anonymous ? "(Anônima)" : "—")}</div>
              <div><b>Comunidade:</b> {selected.community || "—"}</div>
              <div><b>Contato:</b> {selected.contact || "—"} {selected.wants_return && "· Deseja retorno"}</div>
              <div><b>Registrada em:</b> {new Date(selected.created_at).toLocaleString("pt-BR")}</div>
              <div className="mt-3">
                <div className="text-xs uppercase tracking-[0.18em] text-forest-700 mb-1">Descrição</div>
                <div className="bg-cream-100 border border-cream-400 p-3 whitespace-pre-wrap">{selected.description}</div>
              </div>
              <div className="mt-3">
                <div className="text-xs uppercase tracking-[0.18em] text-forest-700 mb-2">Mudar status</div>
                <div className="flex flex-wrap gap-2">
                  {OUV_STATUS.map((s) => (
                    <button key={s.v} onClick={() => changeStatus(selected.id, s.v)} data-testid={`ouv-set-${s.v}`}
                      className={`text-xs px-3 py-1.5 ${selected.status === s.v ? "bg-forest-700 text-cream-100" : "bg-white border border-cream-400 hover:bg-cream-100"}`}>{s.label}</button>
                  ))}
                </div>
              </div>
              {selected.history && (
                <div className="mt-3">
                  <div className="text-xs uppercase tracking-[0.18em] text-forest-700 mb-1">Histórico</div>
                  <ul className="space-y-1">
                    {selected.history.slice().reverse().map((h, i) => (
                      <li key={i} className="text-xs bg-white border border-cream-400 p-2">
                        <span className="text-forest-700 font-medium">{new Date(h.at).toLocaleString("pt-BR")}</span>
                        · {OUV_STATUS.find((x) => x.v === h.status)?.label || h.status}
                        {h.note && <div className="mt-0.5 text-forest-900/80">{h.note}</div>}
                        <div className="text-forest-900/50 mt-0.5">por {h.by}</div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {selected.contact && (
                <a href={`https://wa.me/${selected.contact.replace(/\D/g, "")}`} target="_blank" rel="noreferrer"
                  data-testid="ouv-wa-contact" className="inline-block mt-3 px-4 py-2 bg-[#25D366] text-white text-sm">
                  Responder por WhatsApp
                </a>
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
